import React, { ChangeEvent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import MpTheme from '../../../config/MpTheme'
import { Dialog, DialogActions, DialogContent } from '@mui/material'
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import {
  CorporateCodePost,
  createCorporateCode,
  updateCorporateCode,
  deactivateCorporateCode,
} from '../../../redux/corporateCodeService'
import { useAppDispatch } from '../../../redux/storeExports'
import { fonts } from '../../../config/SigninTheme'
import { useNavigate, useParams } from 'react-router-dom'

export type CorporateFormProps = {
  submitLabel: string
  onSubmit?: () => void
  corporateCodeToEdit?: CorporateCodePost
}

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

const entitlements_list = [
  'meprism:databroker200',
  'meprism:breach',
  'meprism:ppc',
  'meprism:4screen',
]

export const CorporateCodeForm = ({
  submitLabel,
  onSubmit,
  corporateCodeToEdit,
}: CorporateFormProps) => {
  const { handleSubmit } = useForm()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const params = useParams()
  const [entitlmentName, setEntitlmentName] = React.useState<string[]>([])
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openAllowLoginModal, setOpenAllowLoginModal] = useState(false)
  const [formData, setFormData] = useState<CorporateCodePost>({
    active: true,
    code: '',
    description: '',
    entitlements: [],
    owner_buid: '',
    title: '',
    cost_per_employee_cents: 150,
    type: 'default',
    federation_type: null,
    account_type: '',
    is_login_enabled: false,
  })

  useEffect(() => {
    if (corporateCodeToEdit) {
      setFormData({
        ...corporateCodeToEdit,
        type: corporateCodeToEdit?.authentication?.type || 'default',
        federation_type: corporateCodeToEdit?.authentication?.federation_opts?.type || null,
      })
      setEntitlmentName(corporateCodeToEdit.entitlements)
    }
  }, [corporateCodeToEdit])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleConfirmDeleteUsers = async (deleteFlag: boolean) => {
  setOpenDeleteModal(false)
      if (corporateCodeToEdit) {
        const corp_code = params?.corp_code ?? ''
      await dispatch(
        deactivateCorporateCode({
            corp_code,
          deleteFlag,
          }),
        ).unwrap()
    }
          navigate('/home/manage', { replace: true })
        }

  const handleSwitch = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.checked

    if (corporateCodeToEdit?.active === true && corporateCodeToEdit?.active !== value) {
      if (corporateCodeToEdit.account_type === 'executive') {
        setOpenDeleteModal(true)
        setFormData({
          ...formData,
          active: value,
        })
        return
      }
      else if(corporateCodeToEdit.account_type === 'standard'){
        setOpenDeleteModal(true)
        setFormData({
          ...formData,
          active: value,
        })
        return
      }
    }
    setFormData({
      ...formData,
      active: value,
    })
  }

  const handleAccountTypeChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      account_type: event.target.value,
    })
  }


  const handleAuthenticationChange = (event: SelectChangeEvent) => {
    const federated = event.target.value === 'federation'
    setFormData({
      ...formData,
      type: federated ? 'federation' : 'default',
      federation_type: federated ? 'okta' : null,
    })
  }

  const handleSelectChange = (
    event: SelectChangeEvent<typeof entitlmentName>,
  ) => {
    const {
      target: { value },
    } = event
    setEntitlmentName(typeof value === 'string' ? value.split(',') : value)
    let selectedEntitlement = value as string[]
    setFormData({
      ...formData,
      entitlements: selectedEntitlement,
    })
  }

  const transformFormData = (data: any) => {
    const newObject = {
      ...data,
      authentication: {
        type: data.type,
        federation_type: data.type === 'federation' ? data.federation_type : null,
      },
      is_login_enabled: data.account_type === 'standard' ? false : data.is_login_enabled,
    }
    delete newObject['type']
    delete newObject['federation_type']

    return newObject
  }

  async function onSubmitData() {

    try {
      if (corporateCodeToEdit) {
        const corp_code = params?.corp_code ?? ''
        const res = await dispatch(
          updateCorporateCode({
            corp_code,
            formData: transformFormData(formData),
          }),
        )
        if (res.payload) navigate('/home/manage', { replace: true })
      } else {
        const res = await dispatch(
          createCorporateCode(transformFormData(formData)),
        )
        if (res.payload) navigate('/home/manage', { replace: true })
      }
    } catch (error) {
      console.error('An error occurred:', error)
    }
  }

  const handleAllowUserToLoginSwitch = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!formData || formData.account_type !== 'executive') {
      return
    }
    if (corporateCodeToEdit?.is_login_enabled !== !formData.is_login_enabled){
    setOpenAllowLoginModal(true)
  }
    else{
      setFormData({
        ...formData,
        is_login_enabled: !formData.is_login_enabled
      })
    }
  }

  const handleConfitmAllowLogin = () => async () => {
    setOpenAllowLoginModal(false)
    setFormData({
      ...formData,
      is_login_enabled: !formData.is_login_enabled,
    })
    
  }
  
  return (
    <form onSubmit={handleSubmit(onSubmitData)}>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: MpTheme.layouts.widths.md,
          width: '100%',
          mx: 'auto',
        }}>
        <Stack
          spacing={5}
          sx={{
            maxWidth: MpTheme.layouts.widths.md,
            width: '100%',
            mx: 'auto',
          }}>
          
          {/* Code*/}
          <Box>
            <Typography variant={'h2'} sx={{ whiteSpace: 'break-spaces' }}>
              Code
            </Typography>
            <TextField
              name="code"
              placeholder="Code"
              required
              fullWidth
              disabled={!!corporateCodeToEdit}
              value={formData.code}
              InputProps={{ inputMode: 'numeric' }}
              onChange={handleChange}
              sx={{
                height: '38px',
                '& .MuiInput-root': {
                  height: '42px',
                },
                '& .MuiInputLabel-root': {
                  marginBottom: '5px',
                },
              }}
            />
          </Box>
          
          {/* Title*/}
          <Box>
            <Typography variant={'h2'} sx={{ whiteSpace: 'break-spaces' }}>
              Title
            </Typography>
            <TextField
              name="title"
              placeholder="Title"
              required
              fullWidth
              value={formData.title}
              InputProps={{ inputMode: 'numeric' }}
              onChange={handleChange}
              sx={{
                height: '38px',
                '& .MuiInput-root': {
                  height: '42px',
                },
                '& .MuiInputLabel-root': {
                  marginBottom: '5px',
                },
              }}
            />
          </Box>
          
          {/* Description*/}
          <Box>
            <Typography variant={'h2'} sx={{ whiteSpace: 'break-spaces' }}>
              Description
            </Typography>
            <TextField
              name="description"
              placeholder="Description"
              required
              fullWidth
              value={formData.description}
              onChange={handleChange}
              sx={{
                height: '38px',
                '& .MuiInput-root': {
                  height: '40px',
                },
              }}
            />
          </Box>
          
          {/* Cost Per Employee*/}
          <Box>
            <Typography variant={'h2'} sx={{ whiteSpace: 'break-spaces' }}>
              Cost Per Employee
            </Typography>
            <TextField
              name="cost_per_employee_cents"
              placeholder="Max Redemption"
              required
              fullWidth
              onChange={handleChange}
              value={formData.cost_per_employee_cents}
              sx={{
                height: '38px',
                '& .MuiInput-root': {
                  height: '40px',
                },
              }}
            />
          </Box>
          
          {/* Owner Buid*/}
          <Box>
            <Typography variant={'h2'} sx={{ whiteSpace: 'break-spaces' }}>
              Owner BUID
            </Typography>
            <TextField
              name="owner_buid"
              placeholder="Owner BUID"
              disabled={!!corporateCodeToEdit}
              required
              fullWidth
              onChange={handleChange}
              value={formData.owner_buid}
              sx={{
                height: '38px',
                '& .MuiInput-root': {
                  height: '40px',
                },
              }}
            />
          </Box>

          {/* Entitlements*/}
          <Box>
            <FormControl sx={{ m: 1, width: '100%' }}>
              <InputLabel id="demo-multiple-name-label">Entitlement</InputLabel>
              <Select
                labelId="demo-multiple-name-label"
                id="demo-multiple-name"
                multiple
                value={entitlmentName}
                onChange={handleSelectChange}
                required
                input={<OutlinedInput label="Name" />}
                MenuProps={MenuProps}>
                {entitlements_list.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
           {/* Account Type*/}
          <Box>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">
                Account Type
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={formData.account_type ? formData.account_type : ""}
                label="Account Type"
                onChange={handleAccountTypeChange}
              >
                <MenuItem value="executive">Executive</MenuItem>
                <MenuItem value="standard">Standard</MenuItem>
              </Select>

            </FormControl>
          </Box>
          
          {/* allow_user_to_login switch */}
          {formData && formData.account_type === 'executive' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <Typography variant={'subheader'}>Enable Login</Typography>
            <Switch checked={!!formData.is_login_enabled} onChange={handleAllowUserToLoginSwitch} />
          </Box>
          )}
          
          {/* Status switch*/}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <Typography variant={'subheader'}>Status</Typography>
            <Switch checked={formData.active} onChange={handleSwitch} />
          </Box>
          
          {/* Authentication Type*/}
          <Box sx={{mt: '10px' }}>
            <FormControl fullWidth>
              <InputLabel>Authentication Type</InputLabel>
              <Select
                value={formData.type}
                label="Authentication Type"
                onChange={handleAuthenticationChange}>
                <MenuItem value="default">default</MenuItem>
                <MenuItem value="federation">federation</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ mt: '15px' }}>
              {formData?.type === 'federation' && (
                <>
                  <InputLabel sx={{ mt: '15px' }}>Federation Type</InputLabel>
                  <Select value={formData.federation_type} label="Faderated Type" disabled>
                    <MenuItem value="okta">Okta</MenuItem>
                  </Select>
                  <InputLabel sx={{ mt: '15px' }}>Federation Name</InputLabel>
                  <TextField
                    value={`Okta-${formData.code}`.toLowerCase()}
                    disabled
                  />
                </>
              )}
            </Box>
            <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
              <DialogContent>
                <Typography>
                  {corporateCodeToEdit?.account_type === 'executive' ? (
                    <Typography>
                      Are you sure you want to deactivate the corporate code{' '}
                      <strong>{corporateCodeToEdit?.code}</strong>?
                    </Typography>
                  ) : (
                    <Typography>
                      Do you want to delete all users’ data which belongs to{' '}
                      <strong>{corporateCodeToEdit?.code}</strong>?
                    </Typography>
                  )}
                </Typography>
              </DialogContent>
             
              <DialogActions>
                {corporateCodeToEdit?.account_type === 'executive' ? (
                  <>
                    <Button onClick={() => setOpenDeleteModal(false)}>Cancel</Button>
                    <Button color="error" onClick={() => handleConfirmDeleteUsers(true)}>
                      Deactivate
                    </Button>
                  </>
                ) : (
                  <>
                <Button onClick={() => handleConfirmDeleteUsers(false)}>Keep Data</Button>
                <Button color="error" onClick={() => handleConfirmDeleteUsers(true)}>
                  Delete Data
                </Button>
                  </>
                )}
              </DialogActions>
            </Dialog>

            {/* Allow Login Modal */}
            <Dialog open={openAllowLoginModal} onClose={() => setOpenAllowLoginModal(false)}>
              <DialogContent>
                <Typography>
                  Are you sure you want to {corporateCodeToEdit?.is_login_enabled ? 'disallow' : 'allow'} users to login with the corporate code{' '}
                  <strong>{corporateCodeToEdit?.code}</strong>?
                </Typography>
              </DialogContent>
             
              <DialogActions>
                <Button color="inherit" onClick={() => setOpenAllowLoginModal(false)}>
                  Cancel
                </Button>
                <Button color="primary" onClick={handleConfitmAllowLogin()}>
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Stack>
        <Box sx={{ mt: 5 }}>
          <Button
            variant={'contained'}
            type={'submit'}
            color={'darkPurple'}
            sx={{
              background: '#426BF0',
              width: '100%',
              '&.Mui-disabled': {
                background: '#426BF0',
                fontFamily: fonts.Inter.SemiBold,
                color: '#ffffff',
              },
            }}>
            {submitLabel}
          </Button>
        </Box>
      </Stack>
    </form>
  )
}